import React, {useCallback, useMemo} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {BarElement, CategoryScale, Chart as ChartJS, LinearScale, Title, Tooltip as ChartTooltip} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip);

interface DiagnosticImportanceProps {
    cutSets: MinimalCutSet[];
    nodeMap: Map<number, FaultTreeNode>;
    nodeNameMap: Map<number, string>;
    totalProbability: number;
    structuralHubIds?: Set<number>;
    onBarClick?: (baseEventId: number) => void;
    chartRef?: React.RefObject<ChartJS | null>;
}

interface DiagnosticRow {
    id: number;
    name: string;
    isBarrier: boolean;
    isHub: boolean;
    probability: number;
    /** Fraction of top event probability attributable to cut sets containing this event (FV) */
    fv: number;
    /** Diagnostic importance: P(this event caused it | top event occurred). Normalized so all sum to ~1. */
    diagnostic: number;
    /** Number of cut sets containing this event */
    cutSetCount: number;
}

const DiagnosticImportance: React.FC<DiagnosticImportanceProps> = ({
    cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds, onBarClick, chartRef,
}) => {
    const rows = useMemo((): DiagnosticRow[] => {
        if (totalProbability === 0) return [];

        const allIds = new Set<number>();
        cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));

        // Compute raw FV for each event
        const rawRows = Array.from(allIds).map((id) => {
            const node = nodeMap.get(id);
            const prob = node?.probability ?? 0;
            const containingCS = cutSets.filter((cs) => cs.baseEventIds.has(id));
            const fv = containingCS.reduce((sum, cs) => sum + cs.probability, 0) / totalProbability;

            return {
                id,
                name: nodeNameMap.get(id) || `ID:${id}`,
                isBarrier: node?.nodeClass === 'BARRIER',
                isHub: structuralHubIds?.has(id) ?? false,
                probability: prob,
                fv,
                diagnostic: 0, // computed after normalization
                cutSetCount: containingCS.length,
            };
        });

        // Normalize FV values so they sum to 1 → diagnostic importance
        const fvSum = rawRows.reduce((sum, r) => sum + r.fv, 0);
        if (fvSum > 0) {
            for (const row of rawRows) {
                row.diagnostic = row.fv / fvSum;
            }
        }

        return rawRows.sort((a, b) => b.diagnostic - a.diagnostic);
    }, [cutSets, nodeMap, nodeNameMap, totalProbability, structuralHubIds]);

    const chartData = useMemo(() => ({
        labels: rows.map((r) => r.name),
        datasets: [{
            label: 'Failure Attribution',
            data: rows.map((r) => r.diagnostic * 100),
            backgroundColor: rows.map((r) =>
                r.isBarrier ? 'rgba(76, 175, 80, 0.7)'
                    : r.isHub ? 'rgba(237, 108, 2, 0.7)'
                        : 'rgba(25, 118, 210, 0.7)'
            ),
            borderColor: rows.map((r) =>
                r.isBarrier ? 'rgba(76, 175, 80, 1)'
                    : r.isHub ? 'rgba(237, 108, 2, 1)'
                        : 'rgba(25, 118, 210, 1)'
            ),
            borderWidth: 1,
        }],
    }), [rows]);

    const options = useMemo(() => ({
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: 'If the top event occurred, which base events most likely caused it?',
                font: { size: 12 },
                color: '#666',
            },
            tooltip: {
                callbacks: {
                    label: (ctx: { dataIndex: number; raw: unknown }): string[] => {
                        const row = rows[ctx.dataIndex];
                        const pct = (ctx.raw as number).toFixed(1);
                        const lines = [`Attribution: ${pct}%`];
                        lines.push(`In ${row.cutSetCount} of ${cutSets.length} cut sets`);
                        lines.push(`P = ${row.probability < 0.001 ? row.probability.toExponential(2) : row.probability.toPrecision(3)}`);
                        return lines;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Likelihood of being the cause (%)' },
                min: 0,
                max: Math.min(100, Math.ceil((rows[0]?.diagnostic ?? 0) * 120)),
                ticks: {
                    callback: (value: string | number): string => `${value}%`,
                },
            },
            y: {
                ticks: {
                    font: { size: 11 },
                    autoSkip: false,
                },
            },
        },
        onClick: (_event: unknown, elements: Array<{ index: number }>): void => {
            if (elements.length > 0 && onBarClick) {
                onBarClick(rows[elements[0].index].id);
            }
        },
    }), [rows, cutSets.length, onBarClick]);

    const handleExportCSV = useCallback((): void => {
        const header = ['Basic Event', 'Probability', 'Cut Sets', 'RC (%)', 'Diagnostic (%)'].join(',');
        const csvRows = rows.map((row) =>
            [
                `"${row.name.replace(/"/g, '""')}"`,
                row.probability === 0 ? '0' : row.probability.toExponential(2),
                row.cutSetCount,
                (row.fv * 100).toFixed(1),
                (row.diagnostic * 100).toFixed(1),
            ].join(',')
        );
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `attribution_failure_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [rows]);

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Attribution Failure requires non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    const chartHeight = Math.max(200, rows.length * 24 + 60);

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1 }}>
                <Typography variant="subtitle2">Failure Attribution</Typography>
                <Tooltip
                    title="Given that the top event has occurred, what is the probability that each base event was a contributing cause? Derived from Risk Contribution measure, normalized so contributions sum to 100%."
                    arrow
                >
                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                        (?)
                    </Typography>
                </Tooltip>
                <Tooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            {rows.some((r) => r.isHub) && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                    Orange bars indicate structural hubs — events appearing in ≥50% of cut sets.
                </Typography>
            )}
            <Box sx={{ px: 2, py: 1, overflow: 'auto', maxHeight: 400 }}>
                <Box sx={{ height: chartHeight, minHeight: 200 }}>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    <Bar ref={chartRef as any} data={chartData} options={options} />
                </Box>
            </Box>
        </Box>
    );
};

export default DiagnosticImportance;
